'use client'
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import dynamic from 'next/dynamic'
import { useLazyQuery } from '@apollo/client'

// import styles from './style.module.scss'
import { STATS } from '@/graphql/series/stats.query'
import { cMerge, convertDt24hFormat, dateCheck, getSlugUrl } from '@/shared/utils'
// import tableItemStyles from '@assets/scss/components/table-item.module.scss'
// import CustomSelect from '@/shared/components/customSelect'
import CustomLink from '@/shared/components/customLink'
import { Row } from '@/shared/components/row'
import { useRouter } from 'next/navigation'
import NoData from '@/shared/components/noData'

const CustomSelect = dynamic(() => import('@/shared/components/customSelect'))
const ThemeTable = dynamic(() => import('@/shared/components/themeTable'))

type StatsTableProps = {
  data: any
  id: string
  slug: string[]
}

const StatsTable = ({ data, slug, id }: StatsTableProps) => {
  const router = useRouter()
  const initCallRef = useRef(false)
  const grpTitleRef = useRef()
  const typeData = data?.seriesData?.fetchSeriesStatsTypes
  const [selectedItem, setSelectedItem] = useState({
    sDescription: null,
    _id: null
  })
  useLayoutEffect(() => {
    const currentRoute = slug.at(-1)
    const type = currentRoute?.replaceAll('-', '_')
    const currentItem = typeData?.filter((item: any) => {
      const slugType = item.sSeoType || item.sType
      return slugType === type
    })
    const selected = currentItem?.length > 0 ? currentItem[0] : optionType[0]?.options[0]
    changeLabels(selected?.sType)
    grpTitleRef.current = selected?.eGroupTitle
    setSelectedItem({ sDescription: currentItem[0]?.sDescription, _id: currentItem[0]?._id })
    getStats({
      variables: {
        input: {
          iSeriesId: id,
          _id: currentItem[0]?._id,
          eFormat: changeStats.current.matchType
        }
      }
    }).then(onChangeStats => setStateData(onChangeStats?.data?.fetchSeriesStats))
  }, [])
  // const firstTimeLoaderCheck = useRef(false)
  const optionMatchType = data?.matchTypeData?.listSeriesStatsFormat?.map((type: any) => ({
    value: type,
    label: type?.toUpperCase()
  }))
  const [labels, setLabels] = useState([
    'No',
    'Player',
    'Runs',
    'Match',
    'Inns',
    'Avg',
    'SR',
    '4s',
    '6s'
  ])

  const changeStats = useRef({
    stats: data?.seriesData?.fetchSeriesStatsTypes[0]?._id,
    matchType:
      data?.matchTypeData?.listSeriesStatsFormat && data?.matchTypeData?.listSeriesStatsFormat[0]
  })

  const optionType = [
    {
      sDescription: 'Bat',
      options: data?.seriesData?.fetchSeriesStatsTypes?.filter((e: any) => e?.eGroupTitle === 'Bat')
    },
    {
      sDescription: 'Bowl',
      options: data?.seriesData?.fetchSeriesStatsTypes?.filter((e: any) => e?.eGroupTitle === 'Bwl')
    },
    {
      sDescription: 'Team',
      options: data?.seriesData?.fetchSeriesStatsTypes?.filter(
        (e: any) => e?.eGroupTitle === 'Team'
      )
    }
  ]

  const loaderRangeRef = useRef(9)
  const [getStats] = useLazyQuery(STATS)
  const [stateData, setStateData] = useState<Array<{ dUpdated: string }>>([])
  const [tableData, setTableData] = useState<any>({
    nRuns: 'Runs',
    nMatches: 'Match',
    nInnings: 'Inns',
    nAverage: 'Avg',
    sStrike: 'SR',
    nRun4: '4s',
    nRun6: '6s'
  })
  // useEffect(() => {
  // 	if (changeStats.current && initCallRef.current) {
  // 		getStats({
  // 			variables: {
  // 				input: {
  // 					iSeriesId: id,
  // 					_id: changeStats.current.stats,
  // 					eFormat: changeStats.current.matchType,
  // 				},
  // 			},
  // 		})
  // 	}
  // }, [changeStats.current])

  // useEffect(() => {
  // 	onChangeStats && setStateData(onChangeStats?.fetchSeriesStats)
  // }, [onChangeStats])

  useEffect(() => {
    initCallRef.current = true
  }, [])

  const changeLabels = (description: any) => {
    if (description === 'batting_most_runs') {
      setTableData({
        nRuns: 'Runs',
        nMatches: 'Match',
        nInnings: 'Inns',
        nAverage: 'Avg',
        sStrike: 'SR',
        nRun4: '4s',
        nRun6: '6s'
      })
      setLabels(['No', 'Player', 'Runs', 'Match', 'Inns', 'Avg', 'SR', '4s', '6s'])
      loaderRangeRef.current = 9
    } else if (description === 'batting_most_runs_innings') {
      setTableData({
        nRuns: 'Runs',
        nBalls: 'Balls',
        nAverage: 'Avg',
        sStrike: 'SR',
        nRun4: '4s',
        nRun6: '6s'
      })
      setLabels(['No', 'Batsmen', 'Runs', 'Balls', 'Avg', 'SR', '4s', '6s'])
      loaderRangeRef.current = 8
    } else if (description === 'batting_highest_strikerate') {
      setTableData({
        sStrike: 'SR',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs',
        nAverage: 'Avg'
      })
      setLabels(['No', 'Batsmen', 'SR', 'Matches', 'Inns', 'Runs', 'Avg'])
      loaderRangeRef.current = 7
    } else if (description === 'batting_highest_strikerate_innings') {
      setTableData({
        sStrike: 'SR',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs',
        nAverage: 'Avg'
      })
      setLabels(['No', 'Batsmen', 'SR', 'Matches', 'Inns', 'Runs', 'Avg'])
      loaderRangeRef.current = 7
    } else if (description === 'batting_highest_average') {
      setTableData({
        nAverage: 'Avg',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs',
        nNotout: 'N.O'
      })
      setLabels(['No', 'Batsmen', 'Avg', 'Matches', 'Inns', 'Runs', 'No'])
      loaderRangeRef.current = 7
    } else if (description === 'batting_most_run100') {
      setTableData({
        nRun100: '100s',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs',
        nHighest: 'Hs'
      })
      setLabels(['No', 'Batsmen', '100s', 'Matches', 'Inns', 'Runs', 'HS'])
      loaderRangeRef.current = 7
    } else if (description === 'batting_most_run50') {
      setTableData({
        nRun50: '50s',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs',
        nHighest: 'Hs'
      })
      setLabels(['No', 'Batsmen', '50s', 'Matches', 'Inns', 'Runs', 'HS'])
      loaderRangeRef.current = 7
    } else if (description === 'batting_most_run6') {
      setTableData({
        nRun6: '6s',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs'
      })
      setLabels(['No', 'Batsmen', '6s', 'Matches', 'Inns', 'Runs'])
      loaderRangeRef.current = 6
    } else if (description === 'batting_most_run6_innings') {
      setTableData({
        nRun6: '6s',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs'
      })
      setLabels(['No', 'Batsmen', '6s', 'Matches', 'Inns', 'Runs'])
      loaderRangeRef.current = 6
    } else if (description === 'batting_most_run4') {
      setTableData({
        nRun4: '4s',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs'
      })
      setLabels(['No', 'Batsmen', '4s', 'Matches', 'Inns', 'Runs'])
      loaderRangeRef.current = 6
    } else if (description === 'batting_most_run4_innings') {
      setTableData({
        nRun4: '4s',
        nMatches: 'Match',
        nInnings: 'Inns',
        nRuns: 'Runs'
      })
      setLabels(['No', 'Batsmen', '4s', 'Matches', 'Inns', 'Runs'])
      loaderRangeRef.current = 6
    } else if (description === 'bowling_top_wicket_takers') {
      // Bowl
      setTableData({
        nWickets: 'Wkts',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nBalls: 'Balls',
        nAverage: 'Avg',
        nRuns: 'Runs',
        nWicket4i: '4-Fers',
        nWicket5i: '5-Fers'
      })
      setLabels([
        'No',
        'Bowler',
        'Wkts',
        'Matches',
        'Ovs',
        'Balls',
        'Avg',
        'Runs',
        '4-Fers',
        '5-Fers'
      ])
      loaderRangeRef.current = 10
    } else if (description === 'bowling_best_economy_rates') {
      setTableData({
        nEcon: 'Economy',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nInnings: 'Inns',
        nWickets: 'Wkts',
        nAverage: 'Avg',
        sStrike: 'SR'
      })
      setLabels(['No', 'Bowler', 'Economy', 'Matches', 'Ovs', 'Inns', 'Wkts', 'Avg', 'SR'])
      loaderRangeRef.current = 9
    } else if (description === 'bowling_best_economy_rates_innings') {
      setTableData({
        nEcon: 'Economy',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nInnings: 'Inns',
        nWickets: 'Wkts',
        nAverage: 'Avg',
        sStrike: 'SR'
      })
      setLabels(['No', 'Bowler', 'Economy', 'Matches', 'Ovs', 'Inns', 'Wkts', 'Avg', 'SR'])
      loaderRangeRef.current = 9
    } else if (description === 'bowling_best_bowling_figures') {
      setTableData({
        sBestInning: 'BBI',
        nOvers: 'Ovs',
        nRuns: 'Runs',
        nWickets: 'Wkts',
        nMaidens: 'Maidens',
        nEcon: 'Economy'
      })
      setLabels(['No', 'Bowler', 'BBI', 'Ovs', 'Runs', 'Wkts', 'Maidens', 'Economy'])
      loaderRangeRef.current = 8
    } else if (description === 'bowling_best_strike_rates') {
      setTableData({
        sStrike: 'SR',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Bowler', 'SR', 'Matches', 'Ovs', 'Wkts'])
      loaderRangeRef.current = 6
    } else if (description === 'bowling_best_strike_rates_innings') {
      setTableData({
        sStrike: 'SR',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Bowler', 'SR', 'Matches', 'Ovs', 'Wkts'])
      loaderRangeRef.current = 6
    } else if (description === 'bowling_best_averages') {
      setTableData({
        nAverage: 'Avg',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Bowler', 'Avg', 'Matches', 'Ovs', 'Wkts'])
      loaderRangeRef.current = 6
    } else if (description === 'bowling_most_runs_conceded_innings') {
      setTableData({
        nRunsConceded: 'RCI',
        nOvers: 'Ovs',
        nWickets: 'Wkts',
        sBestInning: 'BBI',
        nMaidens: 'Maidens',
        nEcon: 'Economy'
      })
      setLabels(['No', 'Bowler', 'RCI', 'Ovs', 'Wkts', 'BBI', 'Maidens', 'Economy'])
      loaderRangeRef.current = 8
    } else if (description === 'bowling_four_wickets') {
      setTableData({
        nWicket4i: '4-Fers',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nRuns: 'Runs',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Bowler', '4-Fers', 'Matches', 'Ovs', 'Runs', 'Wkts'])
      loaderRangeRef.current = 7
    } else if (description === 'bowling_five_wickets') {
      setTableData({
        nWicket5i: '5-Fers',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nRuns: 'Runs',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Bowler', '5-Fers', 'Matches', 'Ovs', 'Runs', 'Wkts'])
      loaderRangeRef.current = 7
    } else if (description === 'bowling_maidens') {
      setTableData({
        nMaidens: 'Maidens',
        nMatches: 'Match',
        nOvers: 'Ovs',
        nRuns: 'Runs',
        nWickets: 'Wkts',
        nWicket5i: '5-Fers'
      })
      setLabels(['No', 'Bowler', 'Maidens', 'Matches', 'Ovs', 'Runs', 'Wkts', '5-Fers'])
      loaderRangeRef.current = 8
    } else if (description === 'team_total_runs') {
      // Team
      setTableData({
        nRuns: 'Runs',
        nRun100: '100s',
        nRun50: '50s',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Team', 'Runs', '100s', '50s', 'Wkts'])
      loaderRangeRef.current = 6
    } else if (description === 'team_total_run100') {
      setTableData({
        nRun100: '100s',
        nRuns: 'Runs',
        nRun50: '50s',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Team', '100s', 'Runs', '50s', 'Wkts'])
      loaderRangeRef.current = 6
    } else if (description === 'team_total_run50') {
      setTableData({
        nRun50: '50s',
        nRuns: 'Runs',
        nRun100: '100s',
        nWickets: 'Wkts'
      })
      setLabels(['No', 'Team', '50s', 'Runs', '100s', 'Wkts'])
      loaderRangeRef.current = 6
    } else if (description === 'team_total_wickets') {
      setTableData({
        nWickets: 'Wkts',
        nRunsConceded: 'RUNS Cons',
        nWicket4i: '4-Fers',
        nWicket5i: '5-Fers'
      })
      setLabels(['No', 'Team', 'Wkts', 'RUNS Cons', '4-Fers', '5-Fers'])
      loaderRangeRef.current = 6
    }
  }

  const handleChangeStats = (e: any) => {
    const slugType = e.sSeoType || e.sType
    const url = `/series/${slug?.slice(0, 2)?.join('/')}/${slugType.split('_').join('-')}`
    router.push(url)
  }

  const handleMatchType = (e: any) => {
    e.value !== changeStats.current.matchType && setStateData([])
    changeStats.current = { ...changeStats.current, matchType: e.value }
    changeLabels(changeStats.current.stats)
  }

  return (
    <>
      {typeData?.length !== 0 && (
        <>
          <div className={`flex items-center my-3 justify-between`}>
            <div className="flex items-center w-full">
              <Row className="gap-4 w-full">
                {data?.matchTypeData?.listSeriesStatsFormat && (
                  <div className="col-span-6 sm:col-span-6">
                    <CustomSelect
                      value={optionMatchType[0]}
                      options={optionMatchType}
                      placeholder="Select Match"
                      onChange={(e: any) => handleMatchType(e)}
                      isMedium={true}
                    />
                  </div>
                )}
                <div className="col-span-6 sm:col-span-6">
                  <CustomSelect
                    value={(selectedItem._id && selectedItem) || optionType[0].options[0]}
                    options={optionType}
                    className="m-0"
                    placeholder="Select Type"
                    formatGroupLabel={(opt: any) => opt?.sDescription}
                    getOptionLabel={(opt: any) => opt?.sDescription}
                    getOptionValue={(opt: any) => opt?._id}
                    valueKey="_id"
                    onChange={handleChangeStats}
                    isMedium={true}
                  />
                </div>
              </Row>
              <div className={`items-center justify-end w-full hidden sm:flex`}>
                {stateData?.length > 0 && stateData[0]?.dUpdated && (
                  <div className="text-base font-normal text-t-light-200 ">
                    Last updated on {convertDt24hFormat(dateCheck(stateData[0].dUpdated))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            {stateData?.length > 0 && (
              <ThemeTable
                labels={labels}
                thClass={index => cMerge('w-1/12', index === 1 && '!text-left')}
              >
                {stateData?.map((state: any, index: number) => {
                  return (
                    <tr
                      key={state.key || index}
                      className={`${state.highlight && 'highlight'} font-medium`}
                    >
                      <td className={cMerge('!w-10')}>{index + 1}</td>
                      {grpTitleRef.current === 'Team' ? (
                        <td className={cMerge('w-1/12')}>{state?.oTeam?.sTitle}</td>
                      ) : (
                        <td className={cMerge('w-1/12 !text-left')}>
                          {state?.oPlayer?.eTagStatus === 'a' ? (
                            <CustomLink href={getSlugUrl(`/${state?.oPlayer?.oSeo?.sSlug}`)}>
                              {state?.oPlayer?.sFirstName || state?.oPlayer?.sFullName}
                            </CustomLink>
                          ) : (
                            state?.oPlayer?.sFirstName || state?.oPlayer?.sFullName
                          )}
                        </td>
                      )}
                      {Object.keys(tableData)?.map(value => {
                        return <td key={value}>{state[value] || '--'}</td>
                      })}
                    </tr>
                  )
                })}
              </ThemeTable>
            )}
            {stateData?.length > 0 && stateData[0]?.dUpdated && (
              <div className="text-base font-normal text-t-light-200 sm:hidden">
                Last updated on {convertDt24hFormat(dateCheck(stateData[0].dUpdated))}
              </div>
            )}
            {stateData?.length === 0 && <NoData />}
            {/* <NoData imagePath={tableVector}/> */}
          </div>
        </>
      )}
      {/* {type?.length === 0 && <NoData />} */}
    </>
  )
}

export default StatsTable
